import { useInterfaceDesign } from "../../design/InterfaceDesign";
import { designTokens } from "../../design/tokens";
import type {
  EventKind,
  EventNode as EventNodeType,
  Port,
} from "../../../../util/src/blueprint/types.js";
import type { DataSourceId } from "../../../../util/src/layout/types.js";
import { getFlattenedPathOptions } from "../../../../util/src/layout/data-source-schema.js";
import { getMockLayoutData } from "../../../../util/src/layout/mock.js";
import {
  isControlRoomEventPublishable,
  normalizeShortcut,
  formatShortcutDisplay,
} from "../../../../util/src/blueprint/shortcut-keys.js";
import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsRemoteIcon from "@mui/icons-material/SettingsRemote";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type { SvgIconComponent } from "@mui/icons-material";
import dico from "../../../../util/src/language/dico.js";
import { PortHandle } from "../PortHandle.js";
import { ShortcutCapture } from "./ShortcutCapture.js";
import { useBehaviorPathSchema } from "../BehaviorPathSchemaContext.js";
import React from "react";

import BoltIcon from "@mui/icons-material/Bolt";

const EVENT_KIND_ICON: Record<EventKind, SvgIconComponent> = {
  onChange: SyncIcon,
  onLoad: FlashOnIcon,
  onClick: FlashOnIcon,
  onListEnter: LoginIcon,
  onListExit: LogoutIcon,
  onListFilterExit: LogoutIcon,
  onListFilterEnter: LoginIcon,
  onControlRoom: SettingsRemoteIcon,
  onCustomSceneEvent: BoltIcon,
};

export interface EventNodeProps {
  node: EventNodeType;
  setPortsMap: (
    nodeId: string,
    portId: string,
    element: HTMLElement | null,
  ) => void;
  onPortMouseDown?: (nodeId: string, port: Port) => void;
  onPortMouseUp?: (nodeId: string, port: Port) => void;
  onObjectFieldPathChange?: (nodeId: string, fieldPath: string) => void;
  onTriggerOnInitChange?: (nodeId: string, triggerOnInit: boolean) => void;
  onControlRoomFieldChange?: (
    nodeId: string,
    field:
      | "signalName"
      | "signalDescription"
      | "shortcutKey1"
      | "shortcutKey2"
      | "shortcutKey3"
      | "shortcutBinding"
      | "shortcutActionKey",
    value: string | undefined,
  ) => void;
  onControlRoomShortcutChange?: (
    nodeId: string,
    shortcut: {
      shortcutKey1?: string;
      shortcutKey2?: string;
      shortcutKey3?: string;
    },
  ) => void;
  /** Resolved display name from the scene catalog entry. */
  customEventName?: string;
}

const EventNodeComponent = ({
  node,
  setPortsMap,
  onPortMouseDown,
  onPortMouseUp,
  onObjectFieldPathChange,
  onTriggerOnInitChange,
  onControlRoomFieldChange,
  onControlRoomShortcutChange,
  customEventName,
}: EventNodeProps) => {
  const { mode, colorMode } = useInterfaceDesign();
  const palette = designTokens[colorMode];
  const pathSchema = useBehaviorPathSchema(node.objectSource);
  const behaviorDico = dico().behavior;
  const dataSourcesDico = dico().licensedPortal.layoutEditor
    .dataSources as Record<string, string>;
  const layoutFieldsDico = dico().licensedPortal.layoutEditor.fields as Record<
    string,
    unknown
  >;
  const outputPort = node.ports.find((port) => port.direction === "output");
  if (!outputPort) return null;

  const KindIcon = EVENT_KIND_ICON[node.kind];
  const kindLabel = behaviorDico.events.kinds[node.kind];

  const headerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 8px",
    fontSize: 12,
    fontWeight: 700,
    color: "#fff",
    background:
      "linear-gradient(180deg, #ff8f8f 0%, #d64545 55%, #a83232 100%)",
  };

  const wrapperStyle: React.CSSProperties = {
    borderRadius: 8,
    overflow: "hidden",
    background:
      "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(200, 200, 200, 0.98) 100%)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    border: "1px solid #e5e7eb",
  };

  // ── Simple flow events: onLoad, onListEnter, onListExit ─
  if (
    node.kind === "onLoad" ||
    node.kind === "onClick" ||
    node.kind === "onListEnter" ||
    node.kind === "onListExit"
  ) {
    return (
      <div style={{ ...wrapperStyle, minWidth: 200 }}>
        <>
          <div style={headerStyle}>
            <KindIcon sx={{ fontSize: 14 }} />
            {kindLabel}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
              padding: "2px 8px 2px 8px",
            }}
          >
            <PortHandle
              node={node}
              port={outputPort}
              setPortsMap={setPortsMap}
              onMouseDown={() => onPortMouseDown?.(node.id, outputPort)}
              onMouseUp={() => onPortMouseUp?.(node.id, outputPort)}
              isConnected={false}
            />
          </div>
        </>
      </div>
    );
  }

  if (node.kind === "onListFilterExit" || node.kind === "onListFilterEnter") {
    const targetPort = node.ports.find(
      (port) => port.direction === "input" && port.id === "target",
    );
    return (
      <div style={{ ...wrapperStyle, minWidth: 220 }}>
        <div style={headerStyle}>
          <KindIcon sx={{ fontSize: 14 }} />
          {kindLabel}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "4px 8px",
            gap: 8,
          }}
        >
          {targetPort ? (
            <div style={{ flex: 1, minWidth: 0 }}>
              <PortHandle
                node={node}
                port={targetPort}
                setPortsMap={setPortsMap}
                onMouseDown={() => onPortMouseDown?.(node.id, targetPort)}
                onMouseUp={() => onPortMouseUp?.(node.id, targetPort)}
                isConnected={false}
              />
            </div>
          ) : null}
          <PortHandle
            node={node}
            port={outputPort}
            setPortsMap={setPortsMap}
            onMouseDown={() => onPortMouseDown?.(node.id, outputPort)}
            onMouseUp={() => onPortMouseUp?.(node.id, outputPort)}
            isConnected={false}
          />
        </div>
      </div>
    );
  }

  // ── Custom scene catalog event ───────────────────────────────────────────
  if (node.kind === "onCustomSceneEvent") {
    const eventName = customEventName?.trim() || "-";
    return (
      <div style={{ ...wrapperStyle, minWidth: 200 }}>
        <div
          style={{
            ...headerStyle,
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <KindIcon sx={{ fontSize: 14 }} />
            {kindLabel}
          </span>
          <span style={{ fontWeight: 600, opacity: 0.95 }}>{eventName}</span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
            justifyContent: "flex-end",
            padding: "2px 8px 2px 8px",
          }}
        >
          <PortHandle
            node={node}
            port={outputPort}
            setPortsMap={setPortsMap}
            onMouseDown={() => onPortMouseDown?.(node.id, outputPort)}
            onMouseUp={() => onPortMouseUp?.(node.id, outputPort)}
            isConnected={false}
          />
        </div>
      </div>
    );
  }

  // ── Control Room event ────────────────────────────────────────────────────
  if (node.kind === "onControlRoom") {
    const crDico = (behaviorDico.events as unknown as Record<string, unknown>)
      .controlRoom as Record<string, string>;
    const publishable = isControlRoomEventPublishable(node);
    const combo = normalizeShortcut(
      node.shortcutKey1 ?? "",
      node.shortcutKey2,
      node.shortcutKey3,
    );
    const comboDisplay = combo ? formatShortcutDisplay(combo) : "";
    const labelStyle: React.CSSProperties = {
      fontSize: 11,
      fontWeight: 600,
      color: mode === "modern" ? palette.muted : "#6b7280",
      marginBottom: 2,
    };
    const fieldWrapperStyle: React.CSSProperties = { marginBottom: 6 };

    return (
      <div style={{ ...wrapperStyle, width: 280, boxSizing: "border-box" }}>
        <div
          style={{
            ...headerStyle,
            justifyContent: "space-between",
            background: publishable
              ? "linear-gradient(180deg, #8b5cf6 0%, #6d28d9 55%, #5b21b6 100%)"
              : "linear-gradient(180deg, #9ca3af 0%, #6b7280 55%, #4b5563 100%)",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <KindIcon sx={{ fontSize: 14 }} />
            {kindLabel}
          </span>
          {comboDisplay && (
            <span
              style={{
                fontSize: 10,
                opacity: 0.85,
                background: "rgba(255,255,255,0.15)",
                borderRadius: 4,
                padding: "1px 6px",
              }}
            >
              {comboDisplay}
            </span>
          )}
        </div>

        <div style={{ padding: "8px 8px 4px 8px" }}>
          <div style={fieldWrapperStyle}>
            <div style={labelStyle}>{crDico.name}</div>
            <TextField
              size="small"
              value={node.signalName ?? ""}
              placeholder={crDico.namePlaceholder}
              onChange={(e) =>
                onControlRoomFieldChange?.(
                  node.id,
                  "signalName",
                  e.target.value,
                )
              }
              fullWidth
              sx={{
                "& .MuiInputBase-input": { py: 0.45, px: 0.75, fontSize: 12 },
              }}
            />
          </div>

          <div style={fieldWrapperStyle}>
            <div style={labelStyle}>{crDico.description}</div>
            <TextField
              size="small"
              value={node.signalDescription ?? ""}
              placeholder={crDico.descriptionPlaceholder}
              onChange={(e) =>
                onControlRoomFieldChange?.(
                  node.id,
                  "signalDescription",
                  e.target.value,
                )
              }
              fullWidth
              sx={{
                "& .MuiInputBase-input": { py: 0.45, px: 0.75, fontSize: 12 },
              }}
            />
          </div>

          <div style={fieldWrapperStyle}>
            <div style={labelStyle}>{crDico.shortcut}</div>
            <ShortcutCapture
              shortcutKey1={node.shortcutKey1 ?? ""}
              shortcutKey2={node.shortcutKey2}
              shortcutKey3={node.shortcutKey3}
              onChange={(key1, key2, key3) => {
                onControlRoomShortcutChange?.(node.id, {
                  shortcutKey1: key1 || undefined,
                  shortcutKey2: key2,
                  shortcutKey3: key3,
                });
              }}
            />
          </div>

          <TextField
            select
            size="small"
            fullWidth
            label={crDico.preference}
            value={node.shortcutBinding ?? ""}
            helperText={crDico.preferenceHelp}
            onChange={(event) =>
              onControlRoomFieldChange?.(
                node.id,
                "shortcutBinding",
                event.target.value || undefined,
              )
            }
            sx={{ mt: 1 }}
          >
            <MenuItem value="">{crDico.ownShortcut}</MenuItem>
            {[
              ...new Set([
                "validateLift",
                "failLift",
                "resetLift",
                "validateAuto",
                "failAuto",
                ...(node.shortcutBinding ? [node.shortcutBinding] : []),
              ]),
            ].map((key) => (
              <MenuItem key={key} value={key}>
                {dico().keyBinds.keybindName(key) || key}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            fullWidth
            label={crDico.tooltipAction}
            value={node.shortcutActionKey ?? ""}
            helperText={crDico.tooltipActionHelp}
            onChange={(event) =>
              onControlRoomFieldChange?.(
                node.id,
                "shortcutActionKey",
                event.target.value || undefined,
              )
            }
            sx={{ mt: 1 }}
          />

          {!publishable && (
            <Tooltip title={crDico.invalidNode}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 11,
                  color: mode === "modern" ? palette.warning : "#d97706",
                  padding: "2px 0",
                }}
              >
                <WarningAmberIcon sx={{ fontSize: 14 }} />
                {crDico.invalidNode}
              </div>
            </Tooltip>
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            padding: "0 8px 4px 8px",
          }}
        >
          <PortHandle
            node={node}
            port={outputPort}
            setPortsMap={setPortsMap}
            onMouseDown={() => onPortMouseDown?.(node.id, outputPort)}
            onMouseUp={() => onPortMouseUp?.(node.id, outputPort)}
            isConnected={false}
          />
        </div>
      </div>
    );
  }

  // ── onChange nodes ───────────────────────────────────────────────────────────
  const sourceLabel =
    dataSourcesDico[node.objectSource ?? ""] ?? node.objectSource ?? "";
  const mockData = getMockLayoutData();
  const isListSource = Array.isArray(
    mockData[(node.objectSource ?? "") as DataSourceId],
  );
  const pathOptions = getFlattenedPathOptions(pathSchema).map((option) => {
    if (node.objectSource === "currentNode") {
      const currentNodeFields =
        (layoutFieldsDico.currentNode as Record<string, string> | undefined) ??
        {};
      return {
        ...option,
        label: currentNodeFields[option.value] ?? option.label,
      };
    }
    return option;
  });
  const isScalar = isListSource || pathOptions.length === 0;
  const wholeObjectFieldValue = "";
  const fieldOptions = [
    {
      value: wholeObjectFieldValue,
      label: behaviorDico.events.wholeObject,
    },
    ...pathOptions,
  ];
  const selectedFieldPath = node.objectFieldPath ?? wholeObjectFieldValue;
  const resolveFieldOptionLabel = (value: string) =>
    fieldOptions.find((option) => option.value === value)?.label ?? value;

  return (
    <div style={{ ...wrapperStyle, minWidth: 280 }}>
      {/* Scalar onChange - compact: header, then source + port on same line */}
      {isScalar ? (
        <>
          <div style={headerStyle}>
            <KindIcon sx={{ fontSize: 14 }} />
            {kindLabel}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
              padding: "2px 8px 2px 8px",
            }}
          >
            <span
              style={{
                fontSize: 12,
                color: mode === "modern" ? palette.text : "#374151",
                fontWeight: 500,
                width: "100%",
              }}
            >
              {sourceLabel}
            </span>
            <PortHandle
              node={node}
              port={outputPort}
              setPortsMap={setPortsMap}
              onMouseDown={() => onPortMouseDown?.(node.id, outputPort)}
              onMouseUp={() => onPortMouseUp?.(node.id, outputPort)}
              isConnected={false}
            />
          </div>
        </>
      ) : (
        /* Object onChange - header with kind + source, then field selector + port */
        <>
          <div style={{ ...headerStyle, justifyContent: "space-between" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <KindIcon sx={{ fontSize: 14 }} />
              {behaviorDico.events.listener}
            </span>
            <span>{sourceLabel}</span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <TextField
                select
                size="small"
                value={selectedFieldPath}
                onChange={(event) =>
                  onObjectFieldPathChange?.(node.id, event.target.value)
                }
                fullWidth
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) =>
                    resolveFieldOptionLabel(String(selected ?? "")),
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    py: 0.45,
                    px: 0.75,
                    fontSize: 12,
                  },
                }}
              >
                {fieldOptions.map((option) => (
                  <MenuItem
                    key={option.value || "__whole_object__"}
                    value={option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            <div style={{ flexShrink: 0 }}>
              <PortHandle
                node={node}
                port={outputPort}
                setPortsMap={setPortsMap}
                onMouseDown={() => onPortMouseDown?.(node.id, outputPort)}
                onMouseUp={() => onPortMouseUp?.(node.id, outputPort)}
                isConnected={false}
              />
            </div>
          </div>
        </>
      )}
      <div style={{ padding: "0 8px 8px 8px" }}>
        <FormControlLabel
          sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 12 } }}
          control={
            <Checkbox
              size="small"
              checked={Boolean(node.triggerOnInit)}
              onChange={(event) =>
                onTriggerOnInitChange?.(node.id, event.target.checked)
              }
            />
          }
          label={behaviorDico.events.triggerOnInit}
        />
      </div>
    </div>
  );
};

export const EventNode = React.memo(EventNodeComponent);
